import type { RestaurantDemoInventoryItem, RestaurantDemoOrderLine, RestaurantDemoProduct, RestaurantDemoState } from "./restaurant-sandbox";

export interface RestaurantDemoModifierOption {
  readonly id: string;
  readonly name: string;
  readonly priceDeltaMinor: number;
  readonly inventoryDeltas: readonly { readonly inventoryItemId: string; readonly quantity: number }[];
}

export interface RestaurantDemoModifierGroup {
  readonly id: string;
  readonly productId: string;
  readonly name: string;
  readonly required: boolean;
  readonly minSelections: number;
  readonly maxSelections: number;
  readonly options: readonly RestaurantDemoModifierOption[];
}

export interface RestaurantDemoRecipeLine {
  readonly inventoryItemId: string;
  readonly quantity: number;
}

export interface RestaurantDemoRecipe {
  readonly productId: string;
  readonly yieldLabel: string;
  readonly lines: readonly RestaurantDemoRecipeLine[];
}

export interface RestaurantDemoProductVisual {
  readonly productId: string;
  readonly imageUrl: string;
  readonly accent: string;
}

export interface RestaurantDemoCustomerProfile {
  readonly customerId: string;
  readonly tags: readonly string[];
  readonly preferences: readonly string[];
  readonly birthday: string;
  readonly source: string;
}

export interface RestaurantDemoPricingConfig {
  readonly taxRatePct: number;
  readonly serviceChargePct: number;
  readonly defaultDiscountPct: number;
}

export interface RestaurantDemoV5State {
  readonly modifierGroups: readonly RestaurantDemoModifierGroup[];
  readonly recipes: readonly RestaurantDemoRecipe[];
  readonly productVisuals: readonly RestaurantDemoProductVisual[];
  readonly customerProfiles: readonly RestaurantDemoCustomerProfile[];
  readonly pricing: RestaurantDemoPricingConfig;
}

export interface RestaurantDemoConfiguredLine extends RestaurantDemoOrderLine {
  readonly modifiers?: readonly { readonly groupId: string; readonly optionId: string; readonly label: string; readonly priceDeltaMinor: number }[];
}

const STORAGE_PREFIX = "clickdialog:sales-demo:restaurant-v5:";

function storageKey(slug: string): string {
  return `${STORAGE_PREFIX}${slug}`;
}

function baseV5(state: RestaurantDemoState): RestaurantDemoV5State {
  const inventoryByName = new Map(state.inventory.map((item) => [item.name.toLocaleLowerCase("es"), item.id]));
  const muzza = inventoryByName.get("muzzarella") ?? "inv-muzza";
  const harina = inventoryByName.get("harina 000") ?? "inv-harina";
  const tomate = inventoryByName.get("tomate") ?? "inv-tomate";
  const carne = inventoryByName.get("carne vacuna") ?? "inv-carne";
  const burrata = inventoryByName.get("burrata") ?? "inv-burrata";
  const pizzaProducts = state.products.filter((product) => product.category === "Pizzas");

  return Object.freeze({
    modifierGroups: Object.freeze([
      ...pizzaProducts.flatMap((product) => [
        Object.freeze({
          id: `mod-size-${product.id}`,
          productId: product.id,
          name: "Tamaño",
          required: true,
          minSelections: 1,
          maxSelections: 1,
          options: Object.freeze([
            Object.freeze({ id: `opt-med-${product.id}`, name: "Mediana", priceDeltaMinor: 0, inventoryDeltas: Object.freeze([]) }),
            Object.freeze({ id: `opt-large-${product.id}`, name: "Grande", priceDeltaMinor: 400000, inventoryDeltas: Object.freeze([{ inventoryItemId: muzza, quantity: 0.08 }, { inventoryItemId: harina, quantity: 0.08 }]) }),
            Object.freeze({ id: `opt-family-${product.id}`, name: "Familiar", priceDeltaMinor: 700000, inventoryDeltas: Object.freeze([{ inventoryItemId: muzza, quantity: 0.14 }, { inventoryItemId: harina, quantity: 0.12 }]) }),
          ]),
        }),
        Object.freeze({
          id: `mod-extra-${product.id}`,
          productId: product.id,
          name: "Extras",
          required: false,
          minSelections: 0,
          maxSelections: 4,
          options: Object.freeze([
            Object.freeze({ id: `opt-muzza-${product.id}`, name: "Extra muzzarella", priceDeltaMinor: 200000, inventoryDeltas: Object.freeze([{ inventoryItemId: muzza, quantity: 0.08 }]) }),
            Object.freeze({ id: `opt-tomate-${product.id}`, name: "Extra tomate", priceDeltaMinor: 90000, inventoryDeltas: Object.freeze([{ inventoryItemId: tomate, quantity: 0.05 }]) }),
            Object.freeze({ id: `opt-no-garlic-${product.id}`, name: "Sin ajo", priceDeltaMinor: 0, inventoryDeltas: Object.freeze([]) }),
          ]),
        }),
      ]),
    ]),
    recipes: Object.freeze([
      ...pizzaProducts.map((product) => Object.freeze({ productId: product.id, yieldLabel: "1 pizza mediana", lines: Object.freeze([{ inventoryItemId: harina, quantity: 0.25 }, { inventoryItemId: muzza, quantity: 0.18 }, { inventoryItemId: tomate, quantity: 0.12 }]) })),
      Object.freeze({ productId: "prod-empanada", yieldLabel: "1 unidad", lines: Object.freeze([{ inventoryItemId: carne, quantity: 0.09 }]) }),
      Object.freeze({ productId: "prod-burrata", yieldLabel: "1 porción", lines: Object.freeze([{ inventoryItemId: burrata, quantity: 1 }, { inventoryItemId: tomate, quantity: 0.08 }]) }),
    ]),
    productVisuals: Object.freeze(state.products.map((product, index) => Object.freeze({ productId: product.id, imageUrl: "", accent: ["🍕", "🥗", "🥟", "🍮", "🥪", "🍽️"][index % 6]! }))),
    customerProfiles: Object.freeze(state.customers.map((customer, index) => Object.freeze({ customerId: customer.id, tags: Object.freeze(index === 0 ? ["Frecuente", "Alta afinidad"] : ["Cliente activo"]), preferences: Object.freeze(customer.notes ? [customer.notes] : []), birthday: "", source: index === 2 ? "WhatsApp" : "Salón" }))),
    pricing: Object.freeze({ taxRatePct: 0, serviceChargePct: 0, defaultDiscountPct: 0 }),
  });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function createRestaurantDemoV5State(state: RestaurantDemoState): RestaurantDemoV5State {
  return baseV5(state);
}

export function loadRestaurantDemoV5State(slug: string, state: RestaurantDemoState): RestaurantDemoV5State {
  const fallback = baseV5(state);
  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as unknown;
    if (!isObject(parsed)) return fallback;
    return Object.freeze({
      modifierGroups: Object.freeze(Array.isArray(parsed.modifierGroups) ? parsed.modifierGroups as RestaurantDemoModifierGroup[] : fallback.modifierGroups),
      recipes: Object.freeze(Array.isArray(parsed.recipes) ? parsed.recipes as RestaurantDemoRecipe[] : fallback.recipes),
      productVisuals: Object.freeze(Array.isArray(parsed.productVisuals) ? parsed.productVisuals as RestaurantDemoProductVisual[] : fallback.productVisuals),
      customerProfiles: Object.freeze(Array.isArray(parsed.customerProfiles) ? parsed.customerProfiles as RestaurantDemoCustomerProfile[] : fallback.customerProfiles),
      pricing: isObject(parsed.pricing) ? Object.freeze({
        taxRatePct: Number(parsed.pricing.taxRatePct ?? 0),
        serviceChargePct: Number(parsed.pricing.serviceChargePct ?? 0),
        defaultDiscountPct: Number(parsed.pricing.defaultDiscountPct ?? 0),
      }) : fallback.pricing,
    });
  } catch {
    return fallback;
  }
}

export function saveRestaurantDemoV5State(slug: string, state: RestaurantDemoV5State): void {
  window.localStorage.setItem(storageKey(slug), JSON.stringify(state));
}

export function resetRestaurantDemoV5State(slug: string): void {
  window.localStorage.removeItem(storageKey(slug));
}

export function modifierGroupsForProduct(v5: RestaurantDemoV5State, productId: string): readonly RestaurantDemoModifierGroup[] {
  return v5.modifierGroups.filter((group) => group.productId === productId);
}

export function recipeForProduct(v5: RestaurantDemoV5State, productId: string): RestaurantDemoRecipe | null {
  return v5.recipes.find((recipe) => recipe.productId === productId) ?? null;
}

export function visualForProduct(v5: RestaurantDemoV5State, product: RestaurantDemoProduct): RestaurantDemoProductVisual {
  return v5.productVisuals.find((visual) => visual.productId === product.id) ?? { productId: product.id, imageUrl: "", accent: "🍽️" };
}

export function applyInventoryForConfiguredLines(
  state: RestaurantDemoState,
  v5: RestaurantDemoV5State,
  lines: readonly RestaurantDemoConfiguredLine[],
  reason: string,
): RestaurantDemoState {
  const deltas = new Map<string, number>();
  for (const line of lines) {
    const recipe = recipeForProduct(v5, line.productId);
    for (const recipeLine of recipe?.lines ?? []) {
      deltas.set(recipeLine.inventoryItemId, (deltas.get(recipeLine.inventoryItemId) ?? 0) - recipeLine.quantity * line.quantity);
    }
    for (const modifier of line.modifiers ?? []) {
      const group = v5.modifierGroups.find((item) => item.id === modifier.groupId);
      const option = group?.options.find((item) => item.id === modifier.optionId);
      for (const inventoryDelta of option?.inventoryDeltas ?? []) {
        deltas.set(inventoryDelta.inventoryItemId, (deltas.get(inventoryDelta.inventoryItemId) ?? 0) - inventoryDelta.quantity * line.quantity);
      }
    }
  }
  if (!deltas.size) return state;
  const now = new Date().toISOString();
  const inventory = state.inventory.map((item) => Object.freeze({ ...item, onHand: Math.max(0, item.onHand + (deltas.get(item.id) ?? 0)) })) as readonly RestaurantDemoInventoryItem[];
  const movements = [...deltas.entries()].map(([inventoryItemId, delta], index) => Object.freeze({ id: `mov-${Date.now()}-${index}`, inventoryItemId, delta, reason, at: now }));
  return Object.freeze({ ...state, inventory: Object.freeze(inventory), inventoryMovements: Object.freeze([...movements, ...state.inventoryMovements].slice(0, 120)) });
}

export function priceConfiguredLine(line: RestaurantDemoConfiguredLine): number {
  return line.quantity * (line.unitPriceMinor + (line.modifiers ?? []).reduce((sum, modifier) => sum + modifier.priceDeltaMinor, 0));
}
